import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Table, Alert, Spinner, Pagination, Button } from 'flowbite-react';
import { fetchWithAuth } from '../utils/api';
import { getChatHistoryEndpoints } from '../config/api';

interface ChatHistoryItem {
  id: number;
  page_id: string | number;
  sender_id: string | number;
  message: string;
  note?: string | null;
  status: string;
  created_at?: string;
  updated_at?: string;
}

const ChatHistory: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<ChatHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination (giống Pages)
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 10;

  const loadChatHistory = async (page = 1) => {
    try {
      setLoading(true);
      setError(null);

      const endpoint = getChatHistoryEndpoints().LIST;
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: itemsPerPage.toString(),
      });

      const response = await fetchWithAuth(`${endpoint}?${params}`);
      if (response.success && response.data) {
        setItems(response.data.results || []);
        setTotalPages(Math.ceil((response.data.count || 0) / itemsPerPage));
        setTotalItems(response.data.count || 0);
      } else {
        setError('Không thể tải lịch sử trò chuyện');
      }
    } catch (err) {
      setError('Đã xảy ra lỗi khi tải dữ liệu');
      console.error('Load chat history error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadChatHistory(1);
  }, []);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    loadChatHistory(page);
  };

  if (loading && items.length === 0) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Spinner size="xl" />
          <p className="mt-4 text-gray-600">Đang tải lịch sử trò chuyện...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Lịch sử trò chuyện</h1>
        <p className="text-gray-600">Danh sách tin nhắn theo cuộc trò chuyện</p>
      </div>

      {error && (
        <Alert color="failure" className="mb-4">
          <span>{error}</span>
        </Alert>
      )}

      <Card>
        <div className="overflow-x-auto">
          <Table>
            <Table.Head>
              <Table.HeadCell>Page ID</Table.HeadCell>
              <Table.HeadCell>Sender ID</Table.HeadCell>
              <Table.HeadCell>Message</Table.HeadCell>
              <Table.HeadCell>Trạng thái</Table.HeadCell>
              <Table.HeadCell>Hành động</Table.HeadCell>
            </Table.Head>
            <Table.Body className="divide-y">
              {items.map(item => (
                <Table.Row
                  key={item.id}
                  className="bg-white dark:border-gray-700 dark:bg-gray-800"
                >
                  <Table.Cell className="whitespace-nowrap font-medium text-gray-900 dark:text-white">
                    {item.page_id}
                  </Table.Cell>
                  <Table.Cell className="text-gray-500 dark:text-gray-400">
                    {item.sender_id}
                  </Table.Cell>
                  <Table.Cell className="text-gray-500 dark:text-gray-400 max-w-[520px] truncate">
                    {item.message}
                  </Table.Cell>
                  <Table.Cell className="text-gray-500 dark:text-gray-400">
                    {item.status}
                  </Table.Cell>
                  <Table.Cell>
                    <div className="flex space-x-2">
                      <Button
                        size="sm"
                        color="blue"
                        onClick={() => navigate(`/chat-history/${item.id}`)}
                      >
                        Xem
                      </Button>
                    </div>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between p-4">
            <div className="text-sm text-gray-700">
              Hiển thị {(currentPage - 1) * itemsPerPage + 1} đến{' '}
              {Math.min(currentPage * itemsPerPage, totalItems)} trong tổng số{' '}
              {totalItems} kết quả
            </div>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </Card>
    </div>
  );
};

export default ChatHistory;

